import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Button, CircularProgress,
  LinearProgress, Paper, Stack, Divider, IconButton, Tooltip, FormControlLabel, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GppBadIcon from '@mui/icons-material/GppBad';
import {
  getOnboardingList, getOnboardingDetail, sendOnboardingPack,
  verifyOnboardingDocumentIntegrity, reviewOnboardingKyc, downloadOnboardingKyc,
} from '../../api';
import { formatDate, formatDateTime } from '../../utils/dateFormat';

const ACCENT = '#0e7c86';
const ACCENT_DARK = '#0a5a62';

// Status → [label, text color, bg color]
const DOC_STATUS = {
  PENDING:      ['Pending', '#5c7385', '#e9eef1'],
  SENT:         ['Sent', '#5c7385', '#e9eef1'],
  VIEWED:       ['Viewed', '#b3801a', '#f6eeda'],
  SIGNED:       ['Signed', '#b3801a', '#f6eeda'],
  RETURNED:     ['Returned', '#2e9e6b', '#e2f3ea'],
  ACKNOWLEDGED: ['Acknowledged', '#2e9e6b', '#e2f3ea'],
  RECEIVED:     ['Received', '#2e9e6b', '#e2f3ea'],
  DELIVERED:    ['Delivered', '#0a5a62', '#e1f1f2'],
  AWAITING:     ['Awaiting issuance', '#8a9099', '#eef2f5'],
};
const KYC_STATUS = {
  PENDING:  ['Pending', '#ce4b4b', '#f7e6e6'],
  SCANNING: ['Security scan pending', '#b3801a', '#f6eeda'],
  RECEIVED: ['Awaiting HR review', '#0a5a62', '#e1f1f2'],
  IN_REVIEW: ['In review', '#b3801a', '#f6eeda'],
  VERIFIED: ['Verified', '#2e9e6b', '#e2f3ea'],
  REJECTED: ['Rejected', '#ce4b4b', '#f7e6e6'],
  UNVERIFIED: ['Legacy file restricted', '#b3801a', '#f6eeda'],
};

const Pill = ({ map, status }) => {
  const [label, color, bg] = map[status] || [status, '#5c7385', '#e9eef1'];
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7,
      px: 1.1, py: 0.4, borderRadius: 999, fontSize: 12, fontWeight: 600, color, bgcolor: bg, whiteSpace: 'nowrap' }}>
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'currentColor' }} />
      {label}
    </Box>
  );
};

const Kpi = ({ value, sub, label }) => (
  <Box sx={{ flex: 1, minWidth: 120, p: 1.5, border: '1px solid #e2e9ec', borderRadius: 2, bgcolor: '#fff' }}>
    <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#14232b', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
      {value}{sub && <Box component="span" sx={{ fontSize: 14, color: '#9aa7ad', fontWeight: 600 }}>{sub}</Box>}
    </Typography>
    <Typography sx={{ fontSize: 12, color: '#64757d', mt: 0.5 }}>{label}</Typography>
  </Box>
);

function EmployeeRow({ emp, selected, onClick }) {
  const s = emp.summary || {};
  return (
    <Box onClick={onClick}
      sx={{ p: 1.5, borderRadius: 2, cursor: 'pointer', border: '1px solid',
        borderColor: selected ? ACCENT : '#eef2f5', bgcolor: selected ? '#f2f9f9' : '#fff',
        mb: 1, transition: 'all .15s', '&:hover': { borderColor: '#cfe3e5', bgcolor: '#f8fbfb' } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.7}>
            <Typography sx={{ fontWeight: 650, fontSize: 14, color: '#14232b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {emp.name}
            </Typography>
            {emp.is_prospective && (
              <Box component="span" sx={{ flex: 'none', fontSize: 10, fontWeight: 700, color: '#b3801a', bgcolor: '#f6eeda', px: 0.7, py: '1px', borderRadius: 999 }}>ONBOARDING</Box>
            )}
          </Stack>
          <Typography sx={{ fontSize: 12, color: '#8a9099' }}>
            {emp.employee_no}{emp.department ? ` · ${emp.department}` : ''}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: ACCENT_DARK, fontVariantNumeric: 'tabular-nums' }}>
          {s.overall_pct ?? 0}%
        </Typography>
      </Stack>
      <LinearProgress variant="determinate" value={s.overall_pct ?? 0}
        sx={{ mt: 1, height: 5, borderRadius: 3, bgcolor: '#eef2f5',
          '& .MuiLinearProgress-bar': { bgcolor: ACCENT, borderRadius: 3 } }} />
    </Box>
  );
}

export default function OnboardingDashboard() {
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [includeAll, setIncludeAll] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await getOnboardingList({ search: search || undefined, all: includeAll ? 'true' : undefined });
      setList(res.results || []);
    } catch (e) {
      setToast('Could not load onboarding list.');
    } finally {
      setListLoading(false);
    }
  }, [search, includeAll]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openDetail = async (employeeNo) => {
    setSelected(employeeNo);
    setDetailLoading(true);
    try {
      setDetail(await getOnboardingDetail(employeeNo));
    } catch (e) {
      setToast('Could not load employee onboarding.');
    } finally {
      setDetailLoading(false);
    }
  };

  const resend = async () => {
    if (!selected) return;
    setSending(true);
    setToast(null);
    try {
      await sendOnboardingPack(selected);
      setToast('Onboarding pack sent.');
      await openDetail(selected);
      fetchList();
    } catch (e) {
      setToast(e?.response?.data?.error || 'Could not send the pack.');
    } finally {
      setSending(false);
    }
  };

  const copyLink = (url) => {
    if (!url) return;
    try { navigator.clipboard.writeText(url); setToast('Sign link copied.'); } catch (e) { /* ignore */ }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f4f7f8', minHeight: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 750, color: '#14232b', letterSpacing: '-.3px' }}>Employee Onboarding</Typography>
          <Typography sx={{ fontSize: 13, color: '#64757d' }}>Track document delivery, e-signatures and KYC collection for every new hire.</Typography>
        </Box>
        <FormControlLabel
          control={<Switch checked={includeAll} onChange={(e) => setIncludeAll(e.target.checked)} size="small" />}
          label={<Typography sx={{ fontSize: 13, color: '#64757d' }}>Include not-yet-provisioned</Typography>}
        />
      </Stack>

      {toast && (
        <Box sx={{ mb: 2, px: 2, py: 1.2, borderRadius: 2, bgcolor: '#e1f1f2', color: ACCENT_DARK, fontSize: 13, fontWeight: 600 }}>
          {toast}
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '340px 1fr' }, gap: 2, alignItems: 'start' }}>
        {/* Left: employee list */}
        <Paper sx={{ p: 1.5, borderRadius: 3, border: '1px solid #e2e9ec' }}>
          <TextField
            fullWidth size="small" placeholder="Search name, ID or email…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#9aa7ad' }} /></InputAdornment>) }}
            sx={{ mb: 1.5 }}
          />
          {listLoading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: ACCENT }} /></Box>
          ) : list.length === 0 ? (
            <Box sx={{ py: 5, px: 2, textAlign: 'center', color: '#8a9099' }}>
              <Typography sx={{ fontSize: 13 }}>No onboarding records yet.</Typography>
              <Typography sx={{ fontSize: 12, mt: 0.5 }}>New employees appear here automatically when their ID is created. Toggle "Include not-yet-provisioned" to send a pack to an existing employee.</Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: '70vh', overflowY: 'auto', pr: 0.5 }}>
              {list.map((emp) => (
                <EmployeeRow key={emp.employee_no} emp={emp} selected={selected === emp.employee_no}
                  onClick={() => openDetail(emp.employee_no)} />
              ))}
            </Box>
          )}
        </Paper>

        {/* Right: detail */}
        <Box>
          {!selected ? (
            <Paper sx={{ p: 6, borderRadius: 3, border: '1px solid #e2e9ec', textAlign: 'center', color: '#8a9099' }}>
              <DescriptionIcon sx={{ fontSize: 42, color: '#cfdbe0', mb: 1 }} />
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#64757d' }}>Select an employee</Typography>
              <Typography sx={{ fontSize: 13 }}>Pick someone on the left to see their onboarding tracker.</Typography>
            </Paper>
          ) : detailLoading ? (
            <Paper sx={{ p: 6, borderRadius: 3, textAlign: 'center' }}><CircularProgress sx={{ color: ACCENT }} /></Paper>
          ) : detail ? (
            <DetailPanel
              detail={detail}
              sending={sending}
              onResend={resend}
              onCopy={copyLink}
              onReviewed={async (message) => {
                setToast(message);
                await openDetail(selected);
                fetchList();
              }}
            />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

function DetailPanel({ detail, sending, onResend, onCopy, onReviewed }) {
  const emp = detail.employee || {};
  const s = detail.summary || {};
  const provisioned = detail.provisioned;
  const [integrity, setIntegrity] = useState({});
  const [checkingIntegrity, setCheckingIntegrity] = useState(null);
  const [reviewBusy, setReviewBusy] = useState(null);
  const [approveItem, setApproveItem] = useState(null);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [reviewError, setReviewError] = useState('');

  const verifyIntegrity = async (documentId) => {
    setCheckingIntegrity(documentId);
    try {
      const result = await verifyOnboardingDocumentIntegrity(documentId);
      setIntegrity((current) => ({ ...current, [documentId]: result }));
    } catch (error) {
      setIntegrity((current) => ({
        ...current,
        [documentId]: { status: 'ERROR', matches: false },
      }));
    } finally {
      setCheckingIntegrity(null);
    }
  };

  const performReview = async (item, status, reason = '') => {
    setReviewBusy(item.id);
    setReviewError('');
    try {
      const result = await reviewOnboardingKyc(item.id, status, reason);
      setApproveItem(null);
      setRejectItem(null);
      setRejectReason('');
      await onReviewed(result.message);
    } catch (error) {
      setReviewError(error?.response?.data?.error || 'The KYC review could not be saved.');
    } finally {
      setReviewBusy(null);
    }
  };

  const viewKyc = async (item) => {
    setReviewBusy(item.id);
    setReviewError('');
    try {
      const blob = await downloadOnboardingKyc(item.id);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      setReviewError(error?.response?.data?.error || 'The KYC document could not be opened.');
    } finally {
      setReviewBusy(null);
    }
  };

  return (
    <Paper sx={{ borderRadius: 3, border: '1px solid #e2e9ec', overflow: 'hidden' }}>
      {/* header */}
      <Box sx={{ p: 2.5, borderBottom: '1px solid #eef2f5' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ sm: 'flex-start' }}>
          <Box>
            <Typography sx={{ fontSize: 19, fontWeight: 750, color: '#14232b' }}>{emp.name}</Typography>
            <Typography sx={{ fontSize: 13, color: '#64757d' }}>
              {emp.employee_no}{emp.designation ? ` · ${emp.designation}` : ''}{emp.department ? ` · ${emp.department}` : ''}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: '#8a9099', mt: 0.5 }}>
              {emp.email} · {emp.is_prospective ? 'Joining' : 'Joined'} {formatDate(emp.date_of_joining)}{emp.branch_name ? ` · ${emp.branch_name}` : ''}
            </Typography>
          </Box>
          <Button onClick={onResend} disabled={sending} variant="contained"
            startIcon={sending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : (provisioned ? <RefreshIcon /> : <SendIcon />)}
            sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 650, whiteSpace: 'nowrap', '&:hover': { bgcolor: ACCENT_DARK } }}>
            {provisioned ? 'Resend pack' : 'Send onboarding pack'}
          </Button>
        </Stack>

        {/* KPIs */}
        <Stack direction="row" spacing={1.5} sx={{ mt: 2, flexWrap: 'wrap', gap: 1.5 }}>
          <Kpi value={s.overall_pct ?? 0} sub="%" label="Overall complete" />
          <Kpi value={s.signatures_done ?? 0} sub={`/${s.signatures_total ?? 0}`} label="Signatures & acks" />
          <Kpi value={s.kyc_done ?? 0} sub={`/${s.kyc_total ?? 0}`} label="KYC verified" />
          <Kpi value={s.documents_total ?? 0} label="Documents issued" />
        </Stack>
      </Box>

      {/* documents */}
      <Box sx={{ p: 2.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#14232b', mb: 1 }}>Issued by the company</Typography>
        {(detail.documents || []).map((d) => (
          <Stack key={d.id} direction="row" alignItems="center" spacing={1.5}
            sx={{ py: 1.1, borderTop: '1px solid #f2f5f7', '&:first-of-type': { borderTop: 'none' } }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 550, color: '#14232b' }}>{d.title}</Typography>
              <Typography sx={{ fontSize: 11, color: '#9aa7ad', textTransform: 'uppercase', letterSpacing: '.3px' }}>{d.category_display}</Typography>
              {integrity[d.id]?.status === 'VERIFIED' && (
                <Typography sx={{ fontSize: 11, color: '#2e9e6b', fontWeight: 650 }}>Integrity verified</Typography>
              )}
              {integrity[d.id]?.status === 'MISMATCH' && (
                <Typography sx={{ fontSize: 11, color: '#ce4b4b', fontWeight: 700 }}>Tamper alert: checksum mismatch</Typography>
              )}
              {integrity[d.id]?.status === 'ERROR' && (
                <Typography sx={{ fontSize: 11, color: '#ce4b4b' }}>Integrity check failed</Typography>
              )}
            </Box>
            {d.signed_url && (
              <Tooltip title="View signed copy"><IconButton size="small" href={d.signed_url} target="_blank"><PictureAsPdfIcon sx={{ fontSize: 18, color: '#2e9e6b' }} /></IconButton></Tooltip>
            )}
            {d.signed_url && d.integrity_status === 'RECORDED' && (
              <Tooltip title={integrity[d.id]?.status === 'MISMATCH' ? 'Integrity mismatch detected' : 'Verify finalized PDF integrity'}>
                <span>
                  <IconButton size="small" disabled={checkingIntegrity === d.id} onClick={() => verifyIntegrity(d.id)}>
                    {checkingIntegrity === d.id
                      ? <CircularProgress size={16} />
                      : integrity[d.id]?.matches === false
                        ? <GppBadIcon sx={{ fontSize: 18, color: '#ce4b4b' }} />
                        : <VerifiedUserIcon sx={{ fontSize: 18, color: integrity[d.id]?.matches ? '#2e9e6b' : ACCENT_DARK }} />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {d.signed_url && d.integrity_status === 'LEGACY_UNVERIFIED' && (
              <Tooltip title="This legacy signed file has no recorded checksum">
                <GppBadIcon sx={{ fontSize: 18, color: '#b3801a' }} />
              </Tooltip>
            )}
            {d.source_url && (
              <Tooltip title={d.signed_url ? 'View issued document' : 'View document'}>
                <IconButton size="small" href={d.source_url} target="_blank"><VisibilityIcon sx={{ fontSize: 18, color: ACCENT_DARK }} /></IconButton>
              </Tooltip>
            )}
            {d.requires_signature && d.sign_url && !d.is_complete && (
              <Tooltip title="Copy sign link"><IconButton size="small" onClick={() => onCopy(d.sign_url)}><ContentCopyIcon sx={{ fontSize: 16, color: '#8a9099' }} /></IconButton></Tooltip>
            )}
            <Pill map={DOC_STATUS} status={d.status} />
          </Stack>
        ))}

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#14232b', mb: 1 }}>
          Collect from employee
          {detail.kyc_upload_url && (
            <Button size="small" onClick={() => onCopy(detail.kyc_upload_url)} startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
              sx={{ ml: 1, textTransform: 'none', color: ACCENT_DARK, fontSize: 12 }}>Copy upload link</Button>
          )}
        </Typography>
        {reviewError && (
          <Typography sx={{ color: '#ce4b4b', fontSize: 12.5, mb: 1 }}>{reviewError}</Typography>
        )}
        {(detail.kyc_items || []).map((k) => (
          <Box key={k.id} sx={{ py: 1, borderTop: '1px solid #f2f5f7', '&:first-of-type': { borderTop: 'none' } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, color: '#14232b', fontWeight: 550 }}>
                  {k.title}{!k.required && <Box component="span" sx={{ color: '#9aa7ad', fontWeight: 400 }}> (optional)</Box>}
                </Typography>
                {k.status === 'REJECTED' && k.rejection_reason && (
                  <Typography sx={{ color: '#8d4a4a', fontSize: 11.5, mt: 0.25 }}>HR note: {k.rejection_reason}</Typography>
                )}
                {(k.review_history || []).map((entry, index) => (
                  <Typography key={`${entry.event}-${entry.created_at}-${index}`} sx={{ color: '#8a9099', fontSize: 10.5, mt: 0.2 }}>
                    {entry.event === 'review_approved' ? 'Approved' : entry.event === 'review_rejected' ? 'Rejected' : entry.event === 'replacement_uploaded' ? 'Replacement uploaded' : entry.event === 'resubmission_requested' ? 'Resubmission requested' : 'Review started'}
                    {entry.reviewer ? ` by ${entry.reviewer}` : ''}
                    {entry.created_at ? ` - ${formatDateTime(entry.created_at)}` : ''}
                  </Typography>
                ))}
              </Box>
              {k.download_available && (
                <Tooltip title="View security-cleared document">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`View ${k.title}`}
                      disabled={reviewBusy === k.id}
                      onClick={() => viewKyc(k)}
                    >
                      <VisibilityIcon sx={{ fontSize: 18, color: '#2e9e6b' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {k.reviewable && (
                <>
                  <Button size="small" variant="outlined" disabled={reviewBusy === k.id}
                    onClick={() => { setReviewError(''); setRejectItem(k); setRejectReason(''); }}
                    sx={{ textTransform: 'none', color: '#ce4b4b', borderColor: '#efcaca' }}>Reject</Button>
                  <Button size="small" variant="contained" disabled={reviewBusy === k.id}
                    onClick={() => { setReviewError(''); setApproveItem(k); }}
                    sx={{ textTransform: 'none', bgcolor: '#2e9e6b', '&:hover': { bgcolor: '#247f56' } }}>Approve</Button>
                </>
              )}
              <Pill
                map={KYC_STATUS}
                status={k.status === 'RECEIVED' && k.scan_status !== 'CLEAN' ? 'UNVERIFIED' : k.status}
              />
            </Stack>
          </Box>
        ))}
        {(detail.documents || []).some((d) => d.returned_at) && (
          <Typography sx={{ fontSize: 11.5, color: '#9aa7ad', mt: 2 }}>
            Last signed return: {formatDateTime((detail.documents || []).map((d) => d.returned_at).filter(Boolean).sort().pop())}
          </Typography>
        )}
      </Box>
      <Dialog open={!!approveItem} onClose={() => !reviewBusy && setApproveItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve KYC document?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: '#64757d' }}>
            Confirm that you reviewed the security-cleared {approveItem?.title}. Approval applies only to this exact uploaded version.
          </Typography>
          {reviewError && <Typography sx={{ color: '#ce4b4b', fontSize: 12.5, mt: 1 }}>{reviewError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveItem(null)} disabled={!!reviewBusy}>Cancel</Button>
          <Button variant="contained" disabled={!!reviewBusy} onClick={() => performReview(approveItem, 'VERIFIED')}>Approve</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!rejectItem} onClose={() => !reviewBusy && setRejectItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject KYC document</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: '#64757d', mb: 1.5 }}>
            Explain what must be corrected in {rejectItem?.title}. This note will be shown and emailed to the candidate.
          </Typography>
          <TextField autoFocus fullWidth multiline minRows={3} label="Rejection reason" value={rejectReason}
            inputProps={{ maxLength: 1000 }} onChange={(event) => setRejectReason(event.target.value)} />
          {reviewError && <Typography sx={{ color: '#ce4b4b', fontSize: 12.5, mt: 1 }}>{reviewError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectItem(null)} disabled={!!reviewBusy}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!!reviewBusy || !rejectReason.trim()}
            onClick={() => performReview(rejectItem, 'REJECTED', rejectReason)}>Reject & request resubmission</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
